import { X, Search, User, MapPin, Phone, Building2, Calendar, Clock, Stethoscope, ChevronRight, RefreshCcw, Mail, Users, LayoutGrid, ExternalLink } from 'lucide-react';
import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { getAvatarUrl } from '../../../utils/avatar';

const ModalWrapper = ({ isOpen, onClose, title, icon: Icon, children }) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 lg:p-8">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-md animate-in fade-in duration-300"
        onClick={onClose}
      />
      
      {/* Modal Container */}
      <div className="relative bg-white w-full max-w-5xl rounded-[2.5rem] shadow-[0_32px_128px_-16px_rgba(0,0,0,0.3)] overflow-hidden animate-in zoom-in-95 fade-in duration-300 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-6 sm:p-8 border-b flex justify-between items-center bg-white sticky top-0 z-10">
          <div className="flex items-center gap-4 sm:gap-6">
            <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 shadow-inner">
              <Icon className="w-7 h-7" />
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl font-black text-[#143E78] uppercase tracking-tighter">{title}</h3>
              <p className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase mt-1 tracking-widest">Clinical Directory Hub • Real-time Data</p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-3 sm:p-4 bg-slate-100 hover:bg-red-50 hover:text-red-500 rounded-2xl transition-all group flex items-center justify-center"
          >
            <X className="w-6 h-6 group-hover:rotate-90 transition-transform" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto bg-slate-50/30 p-6 sm:p-10 scrollbar-thin">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export const DoctorListPopup = ({ isOpen, onClose, doctors, onSelect }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  
  const getDocName = (doc) => {
    const first = doc.user?.firstName || '';
    const last = doc.user?.lastName || '';
    const full = `${first} ${last}`.trim();
    if (!full) return 'Specialist';
    return full.toLowerCase().startsWith('dr.') ? full : `Dr. ${full}`;
  };

  const filtered = useMemo(() => {
    return doctors.filter(d => 
      getDocName(d).toLowerCase().includes(search.toLowerCase()) ||
      d.specialization?.toLowerCase().includes(search.toLowerCase())
    );
  }, [doctors, search]);

  const surgeons = filtered.filter(d => (/surgeon/i.test(d.doctorType || '') || /surgeon/i.test(d.specialization || '')));
  const anesthesiologists = filtered.filter(d => (/anes|anaes|anesthesiologist/i.test(d.doctorType || '') || /anes|anaes|anesthesiologist/i.test(d.specialization || '')));
  const others = filtered.filter(d => !surgeons.includes(d) && !anesthesiologists.includes(d));

  const renderDoctorGrid = (docs, title) => {
    if (docs.length === 0) return null;
    return (
      <div className="mb-8">
        <h4 className="text-sm font-black text-[#143E78] uppercase tracking-widest mb-4 border-b border-slate-200 pb-2">{title}</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {docs.map((doc) => (
            <div key={doc._id} className="bg-white p-5 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-xl hover:border-blue-100 transition-all group relative overflow-hidden">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl flex items-center justify-center text-blue-600 font-black text-xl shadow-inner overflow-hidden shrink-0">
                  {(() => {
                    const avatarUrl = doc.user?.avatar || doc.documents?.find(d => d.type === 'PHOTO')?.url;
                    if (avatarUrl) {
                      return <img src={getAvatarUrl(avatarUrl)} alt="Profile" className="w-full h-full object-cover" />;
                    }
                    return <>{doc.user?.firstName?.[0] || 'D'}{doc.user?.lastName?.[0] || 'S'}</>;
                  })()}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 
                    onClick={() => { onClose(); navigate(`/doctors/${doc._id}`); }}
                    className="font-black text-[#143E78] uppercase tracking-tighter leading-tight truncate cursor-pointer hover:text-blue-600 flex items-center gap-1.5 transition-colors"
                    title="View Doctor Profile"
                  >
                    {getDocName(doc)}
                    <ExternalLink className="w-3.5 h-3.5 opacity-50" />
                  </h4>
                  <p className="text-[10px] font-black text-blue-500 uppercase tracking-widest mb-3 truncate">{doc.specialization || 'General Surgeon'}</p>
                    
                  <div className="space-y-2 mt-2 pt-3 border-t border-slate-50">
                    <div className="flex items-center gap-2 text-[11px] text-slate-600 font-bold">
                      <Phone className="w-3.5 h-3.5 text-slate-300" /> {doc.user?.phone || 'N/A'}
                    </div>
                    {doc.user?.email && (
                      <div className="flex items-center gap-2 text-[11px] text-indigo-600 font-bold truncate">
                        <Mail className="w-3.5 h-3.5 text-indigo-200" /> {doc.user.email}
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-50">
                       <span className="flex items-center gap-1.5 text-[10px] font-black text-slate-400 uppercase tracking-wider">
                         <Clock className="w-3 h-3" /> {doc.experience || '0'} Yrs Exp
                       </span>
                       {onSelect && (
                         <button 
                           onClick={() => {
                             onSelect(doc._id);
                             onClose();
                           }}
                           className="px-4 py-2 bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-100"
                         >
                           Select
                         </button>
                       )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose} title="Doctor Directory" icon={Stethoscope}>
      <div className="space-y-6">
        <div className="relative group">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
          <input 
            type="text" 
            placeholder="Search by name or specialization..." 
            className="w-full pl-12 pr-4 py-4 bg-white border border-slate-200 rounded-2xl shadow-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-50 transition-all font-bold text-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div>
          {renderDoctorGrid(surgeons, "Surgeons")}
          {renderDoctorGrid(anesthesiologists, "Anesthesiologists")}
          {renderDoctorGrid(others, "Other Specialists")}
          
          {filtered.length === 0 && (
            <div className="py-12 text-center">
              <p className="text-slate-400 font-bold text-sm italic">No specialists found matching "{search}"</p>
            </div>
          )}
        </div>
      </div>
    </ModalWrapper>
  );
};

export const HospitalListPopup = ({ isOpen, onClose, hospitals, onSelect }) => {
  const [search, setSearch] = useState('');
  
  const filtered = useMemo(() => {
    return hospitals.filter(h => 
      h.hospitalName?.toLowerCase().includes(search.toLowerCase()) ||
      h.address?.city?.toLowerCase().includes(search.toLowerCase())
    );
  }, [hospitals, search]);

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose} title="Facility Directory" icon={Building2}>
      <div className="space-y-6">
        <div className="relative group">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
          <input 
            type="text" 
            placeholder="Search by facility name or city..." 
            className="w-full pl-12 pr-4 py-4 bg-white border border-slate-200 rounded-2xl shadow-sm outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-50 transition-all font-bold text-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((hosp) => (
            <div key={hosp._id} className="bg-white p-5 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-xl hover:border-indigo-100 transition-all group">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl flex items-center justify-center text-indigo-600 shadow-inner">
                  <Building2 className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-black text-[#143E78] uppercase tracking-tighter leading-tight truncate mb-1">{hosp.hospitalName}</h4>
                  <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-3 truncate">ID: {hosp.registrationNumber || hosp._id.substring(hosp._id.length - 6).toUpperCase()}</p>
                  
                  <div className="space-y-2 mt-2 pt-3 border-t border-slate-50">
                    <div className="flex items-center gap-2 text-[11px] text-slate-500 font-bold">
                      <MapPin className="w-3.5 h-3.5 text-slate-300" /> {hosp.address?.city}, {hosp.address?.state}
                    </div>
                    <div className="space-y-1.5 mt-2">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.1em] mb-1">Facility Contact</p>
                      <div className="flex items-center gap-2 text-[11px] text-slate-700 font-bold">
                        <User className="w-3.5 h-3.5 text-slate-300" /> {hosp.contactPerson?.name || 'N/A'}
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-slate-600 font-bold">
                        <Phone className="w-3.5 h-3.5 text-slate-300" /> {hosp.contactPerson?.phone || 'N/A'}
                      </div>
                    </div>
                    <div className="flex items-center justify-end mt-4 pt-3 border-t border-slate-50">
                       {onSelect && (
                         <button 
                           onClick={() => {
                             onSelect(hosp._id);
                             onClose();
                           }}
                           className="px-4 py-2 bg-indigo-600 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100"
                         >
                           Select Facility
                         </button>
                       )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full py-12 text-center">
              <p className="text-slate-400 font-bold text-sm italic">No facilities found matching "{search}"</p>
            </div>
          )}
        </div>
      </div>
    </ModalWrapper>
  );
};

export const AvailabilityPopup = ({ isOpen, onClose, doctors, onRefresh }) => {
  const navigate = useNavigate();
  const days = [
    { label: 'M', full: 'Monday' },
    { label: 'T', full: 'Tuesday' },
    { label: 'W', full: 'Wednesday' },
    { label: 'T', full: 'Thursday' },
    { label: 'F', full: 'Friday' },
    { label: 'S', full: 'Saturday' }
  ];

  const availableDocs = useMemo(() => {
    return doctors.filter(doc => (doc.availability?.weeklySchedule || []).some(s => s.isAvailable));
  }, [doctors]);

  const surgeons = availableDocs.filter(d => (/surgeon/i.test(d.doctorType || '') || /surgeon/i.test(d.specialization || '')));
  const anesthesiologists = availableDocs.filter(d => (/anes|anaes|anesthesiologist/i.test(d.doctorType || '') || /anes|anaes|anesthesiologist/i.test(d.specialization || '')));
  const others = availableDocs.filter(d => !surgeons.includes(d) && !anesthesiologists.includes(d));

  const renderDoctorGroup = (docs, title) => {
    if (docs.length === 0) return null;
    return (
      <div className="mb-8">
        <h4 className="text-sm font-black text-[#143E78] uppercase tracking-widest mb-4 border-b border-slate-200 pb-2">{title}</h4>
        <div className="space-y-4">
          {docs.map((doc) => {
            const schedule = doc.availability?.weeklySchedule || [];
            
            return (
              <div key={doc._id} className="bg-white p-5 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col lg:flex-row lg:items-center justify-between gap-4 group hover:border-blue-200 transition-all hover:shadow-md">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center font-black text-blue-600 uppercase text-lg shadow-inner overflow-hidden shrink-0">
                    {(() => {
                      const avatarUrl = doc.user?.avatar || doc.documents?.find(d => d.type === 'PHOTO')?.url;
                      if (avatarUrl) {
                        return <img src={getAvatarUrl(avatarUrl)} alt="Profile" className="w-full h-full object-cover" />;
                      }
                      return <>{doc.user?.firstName?.[0] || 'D'}{doc.user?.lastName?.[0] || 'S'}</>;
                    })()}
                  </div>
                  <div>
                    <h4 
                      onClick={() => { onClose(); navigate(`/doctors/${doc._id}`); }}
                      className="text-base font-black text-[#143E78] uppercase tracking-tighter leading-none mb-1 cursor-pointer hover:text-blue-600 flex items-center gap-1.5 transition-colors"
                      title="View Doctor Profile"
                    >
                      Dr. {doc.user?.firstName} {doc.user?.lastName}
                      <ExternalLink className="w-4 h-4 opacity-50" />
                    </h4>
                    <p className="text-[10px] font-bold text-blue-500 uppercase tracking-[0.2em] mb-2">{doc.specialization || 'Clinical Specialist'}</p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                      <span className="flex items-center gap-1.5 text-[10px] font-bold text-slate-500">
                        <Phone className="w-3 h-3 text-slate-300" /> {doc.user?.phone || 'N/A'}
                      </span>
                      {doc.user?.email && (
                        <span className="flex items-center gap-1.5 text-[10px] font-bold text-indigo-500">
                          <Mail className="w-3 h-3 text-indigo-200" /> {doc.user.email}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-slate-50/50 p-2.5 rounded-2xl border border-slate-100/50 overflow-x-auto">
                  {days.map(day => {
                    const dayData = schedule.find(s => s.day === day.full);
                    const isAvailable = dayData?.isAvailable;
                    const slot = dayData?.timeSlots?.[0];

                    const formatTime = (timeStr) => {
                      if (!timeStr) return '';
                      const [hours, minutes] = timeStr.split(':');
                      let h = parseInt(hours);
                      const ampm = h >= 12 ? 'PM' : 'AM';
                      h = h % 12;
                      h = h ? h : 12;
                      return `${h}:${minutes} ${ampm}`;
                    };
                    
                    return (
                      <div 
                        key={day.full} 
                        className={`min-w-[5rem] p-1.5 rounded-xl flex flex-col items-center justify-center shadow-sm transition-all duration-300 ${isAvailable ? 'bg-emerald-500 text-white scale-105 shadow-emerald-100' : 'bg-white text-slate-300'}`}
                      >
                        <span className="text-[9px] font-black uppercase tracking-tighter leading-none mb-1">{day.label}</span>
                        {isAvailable && slot ? (
                          <div className="flex flex-col items-center">
                            <span className="text-[7px] font-black opacity-80 leading-tight">
                              {formatTime(slot.startTime)}
                            </span>
                            <div className="w-4 h-px bg-white/30 my-0.5" />
                            <span className="text-[7px] font-black opacity-80 leading-tight">
                              {formatTime(slot.endTime)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-[8px] font-bold opacity-40 italic">OFF</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose} title="Doctor Availability" icon={Calendar}>
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between bg-amber-50 p-6 rounded-3xl border border-amber-100 mb-6">
        <p className="text-xs font-bold text-amber-800 leading-relaxed flex-1">
          <b>Live Status:</b> Here you can check which days and times our doctors are free. Green boxes mean the doctor is available.
        </p>
        <button 
          onClick={onRefresh}
          className="flex items-center gap-2 px-4 py-2 bg-white text-amber-600 rounded-xl border border-amber-200 text-[10px] font-black uppercase tracking-widest hover:bg-amber-100 transition-all shadow-sm shrink-0"
        >
          <RefreshCcw className="w-3.5 h-3.5" /> Refresh List
        </button>
      </div>
      
      <div>
        {renderDoctorGroup(surgeons, "Surgeons")}
        {renderDoctorGroup(anesthesiologists, "Anesthesiologists")}
        {renderDoctorGroup(others, "Other Specialists")}
        
        {availableDocs.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-slate-400 font-bold text-sm italic">No doctors with available slots found.</p>
          </div>
        )}
      </div>
    </ModalWrapper>
  );
};
